import { MediaFileAndCampaignPlaylist } from 'models/Playlist';
import React, { useCallback, useMemo, useState } from 'react';
// @ts-ignore
import { useDrag, useDrop } from 'react-dnd';
import { Input } from 'reactstrap';
import { useApiDelete, useApiPut, useInvalidateQuery } from 'hooks/useApi';
import { useToast } from 'hooks/useToast';
import Button from 'components/Button';
import TypeResource from 'enum/TypeResource';
import TypeAction from 'enum/TypeAction';
import useUser from 'hooks/useUser';
import Icon from 'components/Icon';
import { startsWith } from 'lodash';
import Modal from 'components/Modal';
import ToastType from 'enum/ToastType';

type PlaylistItemProps = {
  dragAndDropKeyAccept: string;
  mediaItems: MediaFileAndCampaignPlaylist;
  playlistId: string;
  index: number;
  onMediaReOrderFinish: () => void;
  onMediaReOrderPreview: (dragId: string, hoverId: string) => void;
  onClick?: (mediaItems: MediaFileAndCampaignPlaylist) => void;
  isFirst?: boolean;
  isLast?: boolean;
  moveUp: (itemId: string) => void;
  moveDown: (itemId: string) => void;
};

type DragAndDropOrderType = { itemId: string };
type DragOrderPreviewType = { isDragging: boolean };

const PlaylistItem = ({
  mediaItems,
  onMediaReOrderFinish,
  onMediaReOrderPreview,
  dragAndDropKeyAccept,
  onClick,
  playlistId,
  index,
  isFirst,
  isLast,
  moveUp,
  moveDown
}: PlaylistItemProps) => {
  const user = useUser();
  const [isEditingTime, setIsEditingTime] = useState(false);
  const [editedTime, setEditedTime] = useState(mediaItems.duration || 0);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { mutate, isPending } = useApiPut<MediaFileAndCampaignPlaylist>(
    `/media-manager/playlists/${playlistId}/items/${mediaItems.id}`
  );
  const { mutate: mutateDelete, isPending: isDeleting } = useApiDelete(
    `/media-manager/playlists/${playlistId}/items/${mediaItems.id}`
  );
  const userCanEdit = useMemo(() => {
    return user?.resources?.[TypeResource.PLAYLISTS]?.includes(
      TypeAction.UPDATE
    );
  }, [user]);

  const userCanDelete = useMemo(() => {
    return user?.resources?.[TypeResource.PLAYLISTS]?.includes(
      TypeAction.DELETE
    );
  }, [user]);

  const handleTimeClick = useCallback(
    (duration: number) => {
      if (!userCanEdit) return;
      setIsEditingTime(true);
      setEditedTime(duration);
    },
    [userCanEdit]
  );
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedTime(parseInt(e.target.value));
  };

  const { addToast } = useToast();

  const { invalidate } = useInvalidateQuery(['/media-manager/playlists']);

  const handleTimeSave = useCallback(() => {
    mutate(
      {
        duration: editedTime,
        order: mediaItems.order,
        active: mediaItems.active
      },
      {
        onSuccess: () => {
          invalidate();
          addToast(ToastType.SUCCESS, 'Tempo atualizado com sucesso.');
        },
        onError: (error: any) => {
          const { errors } = error || ({} as any);
          const message =
            errors?.status === 422 ? errors?.detail?.duration : errors?.detail;
          addToast(ToastType.ERROR, message || 'Erro ao atualizar o tempo.');
        }
      }
    );
    setIsEditingTime(false);
  }, [
    editedTime,
    mutate,
    mediaItems.order,
    mediaItems.active,
    addToast,
    invalidate
  ]);

  const handleTimeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleTimeSave();
    } else if (e.key === 'Escape') {
      setIsEditingTime(false);
    }
  };

  const [{ isDragging }, drag] = useDrag<
    DragAndDropOrderType,
    DragAndDropOrderType,
    DragOrderPreviewType
  >(() => ({
    type: dragAndDropKeyAccept,
    item: { itemId: mediaItems.id! },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  }));

  const [, drop] = useDrop<DragAndDropOrderType>(
    {
      accept: dragAndDropKeyAccept,
      hover(item) {
        onMediaReOrderPreview(item.itemId, mediaItems.id!);
      },
      drop() {
        onMediaReOrderFinish();
      }
    },
    [onMediaReOrderPreview, mediaItems.id, onMediaReOrderFinish]
  );

  const handleMediaClick = useCallback(() => {
    onClick?.(mediaItems);
  }, [mediaItems, onClick]);

  const handleOnDelete = useCallback(() => {
    if (!userCanDelete) return;
    mutateDelete(null as any, {
      onError: (response: any) => {
        addToast(
          ToastType.ERROR,
          response.errors?.detail?.toString() || 'Erro ao excluir o item.'
        );
      },
      onSuccess: () => {
        invalidate();
        addToast(ToastType.SUCCESS, 'Item excluído com sucesso');
      }
    });
  }, [addToast, mutateDelete, userCanDelete, invalidate]);

  const handleMoveUp = useCallback(() => {
    moveUp(mediaItems.id!);
    onMediaReOrderFinish();
  }, [moveUp, mediaItems.id, onMediaReOrderFinish]);

  const handleMoveDown = useCallback(() => {
    moveDown(mediaItems.id!);
    onMediaReOrderFinish();
  }, [moveDown, mediaItems.id, onMediaReOrderFinish]);

  return (
    <tr ref={drop}>
      <td colSpan={10} className="p-0">
        <div ref={drag}>
          <div>
            {!isDragging ? (
              <div className="d-flex justify-content-between align-items-center border-top p-3">
                {mediaItems.mediaFile && (
                  <>
                    <div className="mt-2 me-4">
                      <div className="d-flex flex-column">
                        {isFirst ? null : (
                          <Button
                            data-testid="move-up"
                            color="transparent"
                            type="button"
                            className="border-0 p-1 button-order text-primary"
                            icon="chevron-up"
                            onClick={handleMoveUp}
                          />
                        )}
                        {isLast ? null : (
                          <Button
                            data-testid="move-down"
                            color="transparent"
                            type="button"
                            className="border-0 p-1 button-order text-primary"
                            icon="chevron-down"
                            onClick={handleMoveDown}
                          />
                        )}
                      </div>
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <small>Ordem de execução </small>
                      <br />
                      <b>{index + 1}</b>
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <small>Tempo </small>
                      <br />
                      {startsWith(mediaItems.mediaFile?.mineType, 'image') &&
                        (!isEditingTime ? (
                          <span
                            role="button"
                            onClick={() =>
                              handleTimeClick(mediaItems?.duration || 0)
                            }
                          >
                            <b>{mediaItems?.duration} s</b>
                            {userCanEdit && (
                              <Icon
                                icon="pencil"
                                className="ms-1 text-primary"
                              />
                            )}
                          </span>
                        ) : (
                          <span>
                            <Input
                              className="w-50"
                              type="number"
                              value={editedTime}
                              onChange={handleTimeChange}
                              onKeyDown={handleTimeKeyDown}
                            />
                            <span className="d-flex justify-content-end w-50">
                              <Button
                                type="button"
                                color="primary"
                                className="mt-1 ps-1 pb-1 pt-0 pe-0"
                                icon="check-lg"
                                loading={isPending}
                                onClick={handleTimeSave}
                              ></Button>
                              <Button
                                type="button"
                                color="danger"
                                className="mt-1 ms-1 ps-1 pb-1 pt-0 pe-0"
                                icon="x-lg"
                                onClick={() => setIsEditingTime(false)}
                              ></Button>
                            </span>
                          </span>
                        ))}
                      {startsWith(mediaItems.mediaFile?.mineType, 'video') && (
                        <b>{mediaItems?.duration} s</b>
                      )}
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <div
                        role="button"
                        onClick={handleMediaClick}
                        className="img-thumbnail"
                      >
                        <img
                          alt={mediaItems.mediaFile.name}
                          src={mediaItems.thumbnail}
                          className="m-auto d-block"
                          width={100}
                        />
                      </div>
                    </div>
                    <div className="mt-2 ms-4">
                      {userCanDelete && (
                        <Button
                          type="button"
                          loading={isDeleting}
                          icon="trash"
                          className="ms-4 border-0 pe-0 button-delete text-danger"
                          onClick={() => setShowDeleteConfirmation(true)}
                        ></Button>
                      )}
                    </div>
                  </>
                )}
                {mediaItems.campaign && (
                  <>
                    <div className="mt-2 me-4">
                      <div className="d-flex flex-column">
                        {isFirst ? null : (
                          <Button
                            color="transparent"
                            type="button"
                            className="border-0 p-1 button-order text-primary"
                            icon="chevron-up"
                            onClick={handleMoveUp}
                          />
                        )}
                        {isLast ? null : (
                          <Button
                            color="transparent"
                            type="button"
                            className="border-0 p-1 button-order text-primary"
                            icon="chevron-down"
                            onClick={handleMoveDown}
                          />
                        )}
                      </div>
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <small>Ordem de execução </small>
                      <br />
                      <b>{index + 1}</b>
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <small>Tempo </small>
                      <br />
                      <span>
                        <b>
                          {mediaItems.campaign?.medias?.reduce(
                            (acc, media) => acc + (Number(media.duration) || 0),
                            0
                          )}{' '}
                          s
                        </b>
                      </span>
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <small>Campanha </small>
                      <br />
                      <b>{mediaItems.campaign.name}</b>
                    </div>
                    <div>
                      {userCanDelete && (
                        <Button
                          type="button"
                          loading={isDeleting}
                          icon="trash"
                          className="ms-4 border-0 pe-0 button-delete text-danger"
                          onClick={() => setShowDeleteConfirmation(true)}
                        ></Button>
                      )}
                    </div>
                  </>
                )}
                {mediaItems.internalNews && (
                  <>
                    <div className="mt-2 me-4">
                      <div className="d-flex flex-column">
                        {isFirst ? null : (
                          <Button
                            color="transparent"
                            type="button"
                            className="border-0 p-1 button-order text-primary"
                            icon="chevron-up"
                            onClick={handleMoveUp}
                          />
                        )}
                        {isLast ? null : (
                          <Button
                            color="transparent"
                            type="button"
                            className="border-0 p-1 button-order text-primary"
                            icon="chevron-down"
                            onClick={handleMoveDown}
                          />
                        )}
                      </div>
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <small>Ordem de execução </small>
                      <br />
                      <b>{index + 1}</b>
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <small>Tempo </small>
                      <br />
                      {!isEditingTime ? (
                        <span
                          role="button"
                          onClick={() =>
                            handleTimeClick(mediaItems?.duration || 0)
                          }
                        >
                          <b>{mediaItems?.duration} s</b>
                          {userCanEdit && (
                            <Icon icon="pencil" className="ms-1 text-primary" />
                          )}
                        </span>
                      ) : (
                        <span>
                          <Input
                            className="w-50"
                            type="number"
                            value={editedTime}
                            onChange={handleTimeChange}
                            onKeyDown={handleTimeKeyDown}
                          />
                          <span className="d-flex justify-content-end w-50">
                            <Button
                              type="button"
                              color="primary"
                              className="mt-1 ps-1 pb-1 pt-0 pe-0"
                              icon="check-lg"
                              loading={isPending}
                              onClick={handleTimeSave}
                            ></Button>
                            <Button
                              type="button"
                              color="danger"
                              className="mt-1 ms-1 ps-1 pb-1 pt-0 pe-0"
                              icon="x-lg"
                              onClick={() => setIsEditingTime(false)}
                            ></Button>
                          </span>
                        </span>
                      )}
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <small>Notícia </small>
                      <br />
                      <b>{mediaItems.internalNews.title}</b>
                    </div>
                    <div>
                      {userCanDelete && (
                        <Button
                          type="button"
                          loading={isDeleting}
                          icon="trash"
                          className="ms-4 border-0 pe-0 button-delete text-danger"
                          onClick={() => setShowDeleteConfirmation(true)}
                        ></Button>
                      )}
                    </div>
                  </>
                )}
                {mediaItems.externalNews && (
                  <>
                    <div className="mt-2 me-4">
                      <div className="d-flex flex-column">
                        {isFirst ? null : (
                          <Button
                            color="transparent"
                            type="button"
                            className="border-0 p-1 button-order text-primary"
                            icon="chevron-up"
                            onClick={handleMoveUp}
                          />
                        )}
                        {isLast ? null : (
                          <Button
                            color="transparent"
                            type="button"
                            className="border-0 p-1 button-order text-primary"
                            icon="chevron-down"
                            onClick={handleMoveDown}
                          />
                        )}
                      </div>
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <small>Ordem de execução </small>
                      <br />
                      <b>{index + 1}</b>
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <small>Tempo </small>
                      <br />
                      {(mediaItems?.externalNews?.duration || 0) *
                        (mediaItems?.externalNews?.quantityItems || 0)}
                      s
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <small>Notícia </small>
                      <br />
                      <b>{mediaItems.externalNews.name}</b>
                    </div>
                    <div>
                      {userCanDelete && (
                        <Button
                          type="button"
                          loading={isDeleting}
                          icon="trash"
                          className="ms-4 border-0 pe-0 button-delete text-danger"
                          onClick={() => setShowDeleteConfirmation(true)}
                        ></Button>
                      )}
                    </div>
                  </>
                )}
                {mediaItems.externalUrl && (
                  <>
                    <div className="mt-2 me-4">
                      <div className="d-flex flex-column">
                        {isFirst ? null : (
                          <Button
                            color="transparent"
                            type="button"
                            className="border-0 p-1 button-order text-primary"
                            icon="chevron-up"
                            onClick={handleMoveUp}
                          />
                        )}
                        {isLast ? null : (
                          <Button
                            color="transparent"
                            type="button"
                            className="border-0 p-1 button-order text-primary"
                            icon="chevron-down"
                            onClick={handleMoveDown}
                          />
                        )}
                      </div>
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <small>Ordem de execução </small>
                      <br />
                      <b>{index + 1}</b>
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <small>Tempo </small>
                      <br />
                      {mediaItems?.externalUrl.isIndefinite
                        ? 'Indefinido'
                        : `${mediaItems?.externalUrl?.duration || 0} s`}
                    </div>
                    <div className="mt-2 w-100 me-4">
                      <small>URL Externa </small>
                      <br />
                      <a
                        href={mediaItems.externalUrl.url}
                        className="text-truncate-wrap"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {mediaItems.externalUrl.url}
                      </a>
                    </div>
                    <div>
                      {userCanDelete && (
                        <Button
                          type="button"
                          loading={isDeleting}
                          icon="trash"
                          className="ms-4 border-0 pe-0 button-delete text-danger"
                          onClick={() => setShowDeleteConfirmation(true)}
                        ></Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="campaign-medias-list-item">
                <div className="bg-dark-subtle"></div>
              </div>
            )}
          </div>
          <Modal
            primaryButtonAction={handleOnDelete}
            primaryButtonLabel="Excluir"
            title="Atenção!"
            isOpen={showDeleteConfirmation}
            showCloseButton={false}
            toggle={() => setShowDeleteConfirmation((x) => !x)}
            secondaryButtonAction={() => setShowDeleteConfirmation(false)}
            secondaryButtonLabel="Cancelar"
          >
            <p>
              Desaja realmente excluir o item? <br />
              <small>Essa alteração não poderá ser revertida.</small>
            </p>
          </Modal>
        </div>
      </td>
    </tr>
  );
};

export default PlaylistItem;
